import React, { useCallback, useEffect, useMemo, useState } from "react";
import { isPlayStoreApp, openExternalUrl } from "../lib/appMode";

export default function Subscribe() {
  const [loading, setLoading] = useState(false);
  const [checking, setChecking] = useState(false);
  const [openingPortal, setOpeningPortal] = useState(false);
  const [portalAction, setPortalAction] = useState("");
  const [email, setEmail] = useState("");
  const [sessionId, setSessionId] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [member, setMember] = useState(false);
  const [membershipInfo, setMembershipInfo] = useState(null);
  const playStoreApp = isPlayStoreApp();

  const API_BASE = useMemo(() => {
    const raw = (process.env.REACT_APP_API_URL || "").trim();
    const cleaned = raw.replace(/\/$/, "").replace(/\/api$/, "");
    return cleaned || "https://streamsite-v8dd.onrender.com";
  }, []);

  const checkMember = useCallback(
    async (forcedEmail, options = {}) => {
      const normalizedEmail = String(forcedEmail || email || "").trim().toLowerCase();
      const silent = !!options.silent;

      if (!normalizedEmail) {
        if (!silent) alert("Enter the email used at checkout.");
        return;
      }

      try {
        setChecking(true);
        const res = await fetch(
          `${API_BASE}/api/subscriptions/me?email=${encodeURIComponent(normalizedEmail)}`
        );
        const js = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(js?.error || "Unable to check membership.");

        const isActive = !!js?.member?.active;

        setEmail(normalizedEmail);
        setMember(isActive);
        setMembershipInfo(js?.member || null);

        try {
          localStorage.setItem("member_email", normalizedEmail);
          localStorage.setItem("member_status", isActive ? "active" : "inactive");
        } catch {}
      } catch (err) {
        if (!silent) {
          alert(err?.message || "Unable to check membership right now.");
        }
        setMember(false);
        setMembershipInfo(null);
        try {
          localStorage.setItem("member_status", "inactive");
        } catch {}
      } finally {
        setChecking(false);
      }
    },
    [API_BASE, email]
  );

  useEffect(() => {
    const qs = new URLSearchParams(window.location.search);
    const sid = qs.get("session_id") || "";
    setSessionId(sid);

    if (qs.get("success")) {
      setStatusMsg("Payment completed. Your membership is being confirmed.");
    } else if (qs.get("canceled")) {
      setStatusMsg("Checkout was canceled. No payment was taken.");
    } else if (qs.get("billing") === "updated") {
      setStatusMsg("Your billing changes were submitted. Membership status will refresh automatically.");
    }

    let savedEmail = "";
    let cachedStatus = "";

    try {
      savedEmail = localStorage.getItem("member_email") || "";
      cachedStatus = localStorage.getItem("member_status") || "";
    } catch {}

    if (savedEmail) {
      setEmail(savedEmail);
    }

    setMember(cachedStatus === "active");

    if (savedEmail) {
      void checkMember(savedEmail, { silent: true });
    }
  }, [checkMember]);

  async function startSubscribe() {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail) {
      alert("Enter the email you want tied to membership.");
      return;
    }

    if (member) {
      alert("Your membership is already active. Use Manage Billing for subscription changes.");
      return;
    }

    try {
      setLoading(true);
      const res = await fetch(`${API_BASE}/api/subscriptions/create-checkout-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email: normalizedEmail }),
      });
      const js = await res.json().catch(() => ({}));

      if (res.status === 409 || js?.error === "already_active") {
        setMember(true);
        setStatusMsg("Your membership is already active. Use Manage Billing for subscription changes.");
        try {
          localStorage.setItem("member_email", normalizedEmail);
          localStorage.setItem("member_status", "active");
        } catch {}
        return;
      }

      if (!res.ok) {
        throw new Error(js?.error || "Unable to start membership checkout.");
      }

      try {
        localStorage.setItem("member_email", normalizedEmail);
      } catch {}

      if (js?.url) {
        await openExternalUrl(js.url);
      } else {
        throw new Error("Stripe checkout URL was missing.");
      }
    } catch (err) {
      alert(err?.message || "Unable to start membership checkout.");
    } finally {
      setLoading(false);
    }
  }

  async function openPortal(action = "manage") {
    const normalizedEmail = email.trim().toLowerCase();

    if (!normalizedEmail && !sessionId) {
      alert("No billing profile was found yet. Complete checkout first.");
      return;
    }

    try {
      setOpeningPortal(true);
      setPortalAction(action);
      const res = await fetch(`${API_BASE}/api/subscriptions/create-portal-session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ sessionId, email: normalizedEmail, action }),
      });
      const js = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(js?.error || "Unable to open billing portal.");
      if (js?.url) {
        await openExternalUrl(js.url);
      } else {
        throw new Error("Billing portal URL was missing.");
      }
    } catch (err) {
      alert(err?.message || "Unable to open billing portal.");
    } finally {
      setOpeningPortal(false);
      setPortalAction("");
    }
  }

  function formatMembershipDate(value) {
    if (!value) return "";
    const raw = Number(value);
    const date = Number.isFinite(raw)
      ? new Date(raw > 10_000_000_000 ? raw : raw * 1000)
      : new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return new Intl.DateTimeFormat(undefined, {
      month: "long",
      day: "numeric",
      year: "numeric",
    }).format(date);
  }

  const cancelScheduled = !!membershipInfo?.cancelAtPeriodEnd;
  const paidThroughDate = formatMembershipDate(
    membershipInfo?.cancelAt || membershipInfo?.currentPeriodEnd
  );
  const statusLabel = cancelScheduled
    ? "CANCELS AT PERIOD END"
    : member
    ? "ACTIVE"
    : "NOT ACTIVE";
  const stripeLabel = membershipInfo?.stripeStatus || (member ? "active" : "none");

  return (
    <section style={styles.page}>
      <div style={styles.card}>
        <div style={styles.topChip}>
          <span style={styles.dot} />
          GASTREAMING.LIVE • MEMBERSHIP
        </div>

        <h1 style={styles.title}>Become a member</h1>
        <p style={styles.subtitle}>
          {playStoreApp
            ? "Support GAStreaming and unlock paid-member access, ad-free channel viewing, tournaments, giveaways, and future exclusives."
            : "Support GAStreaming and unlock paid-member access, ad-free channel viewing, future exclusives, wagers, tournaments, and giveaways."}
        </p>

        {statusMsg ? <div style={styles.successBox}>{statusMsg}</div> : null}

        <div style={styles.grid}>
          <div style={styles.panel}>
            <div style={styles.panelEyebrow}>Monthly Membership</div>
            <div style={styles.priceRow}>
              <span style={styles.price}>$6</span>
              <span style={styles.priceSuffix}>/ month</span>
            </div>

            <p style={styles.panelText}>
              {playStoreApp
                ? "Unlock paid-member access across GAStreaming, including ad-free stream viewing, tournaments, member perks, giveaways, and future exclusive features."
                : "Unlock paid-member access across GAStreaming, including ad-free stream viewing, wagers, tournaments, member perks, giveaways, and future exclusive features."}
            </p>

            <label style={styles.label}>
              Email for receipt and membership
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                style={styles.input}
              />
            </label>

            <button
              type="button"
              onClick={startSubscribe}
              disabled={loading || checking || member}
              style={{
                ...styles.primaryBtn,
                ...(member ? styles.primaryBtnDisabled : null),
              }}
            >
              {member
                ? "Membership Active"
                : loading
                ? "Starting checkout..."
                : playStoreApp
                ? "Continue in Browser to Subscribe"
                : "Become a Member"}
            </button>
          </div>

          <div style={styles.panel}>
            <div style={styles.panelEyebrow}>Membership Access</div>
            <p style={styles.panelText}>Manage your membership and confirm your current access here.</p>

            <div style={styles.statusBox}>
              <div style={styles.statusTitle}>Status</div>
              <div style={styles.statusValue}>{checking ? "Checking..." : statusLabel}</div>
              <div style={styles.statusMeta}>Stripe status: {checking ? "checking" : String(stripeLabel).toLowerCase()}</div>
              {cancelScheduled && paidThroughDate ? (
                <div style={styles.cancelScheduledBox}>
                  Cancellation scheduled. Your membership stays active through {paidThroughDate} and will not renew after that date.
                </div>
              ) : null}
            </div>

            <div style={styles.actionRow}>
              <button type="button" onClick={() => checkMember()} disabled={checking} style={styles.secondaryBtn}>
                {checking ? "Checking..." : "Refresh Status"}
              </button>

              <button
                type="button"
                onClick={() => openPortal("manage")}
                disabled={openingPortal}
                style={styles.secondaryBtn}
              >
                {openingPortal && portalAction === "manage" ? "Opening..." : "Manage Billing"}
              </button>

              {member && !cancelScheduled ? (
                <button
                  type="button"
                  onClick={() => openPortal("cancel")}
                  disabled={openingPortal}
                  style={styles.cancelBtn}
                >
                  {openingPortal && portalAction === "cancel" ? "Opening cancellation..." : "Cancel Membership"}
                </button>
              ) : null}
            </div>

            <ul style={styles.list}>
              <li>Paid membership skips channel pre-roll ads when you are signed in.</li>
              {playStoreApp ? (
                <li>Paid membership unlocks tournaments and member-only perks.</li>
              ) : (
                <li>Paid membership unlocks access to wagers and tournaments.</li>
              )}
              <li>Members also get access to exclusive perks, giveaways, and future premium features.</li>
              <li>Your membership is tied to your account for access across the site and app.</li>
            </ul>
          </div>
        </div>
      </div>
    </section>
  );
}

const styles = {
  page: {
    minHeight: "calc(100vh - 120px)",
    display: "grid",
    placeItems: "center",
    padding: "28px 16px 36px",
  },
  card: {
    width: "min(1120px, 96vw)",
    borderRadius: 22,
    background: "rgba(15, 10, 14, 0.78)",
    border: "1px solid rgba(255, 30, 30, 0.18)",
    boxShadow: "0 22px 80px rgba(0,0,0,0.55)",
    backdropFilter: "blur(10px)",
    padding: "26px",
  },
  topChip: {
    display: "inline-flex",
    alignItems: "center",
    gap: 10,
    borderRadius: 999,
    padding: "8px 14px",
    border: "1px solid rgba(255,0,0,0.25)",
    background: "rgba(0,0,0,0.28)",
    color: "rgba(255,255,255,0.9)",
    fontSize: 13,
    marginBottom: 18,
  },
  dot: {
    width: 10,
    height: 10,
    borderRadius: "50%",
    background: "#ff2b2b",
    boxShadow: "0 0 0 3px rgba(255,0,0,0.16), 0 0 14px rgba(255,0,0,0.45)",
  },
  title: {
    margin: 0,
    color: "rgba(255,255,255,0.96)",
    fontSize: 32,
    lineHeight: 1.05,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  subtitle: {
    margin: "12px 0 0",
    color: "rgba(235,240,255,0.78)",
    lineHeight: 1.55,
    maxWidth: 820,
  },
  successBox: {
    marginTop: 16,
    borderRadius: 14,
    padding: "12px 14px",
    background: "rgba(24, 118, 67, 0.18)",
    border: "1px solid rgba(66, 196, 114, 0.28)",
    color: "rgba(232,255,239,0.96)",
  },
  grid: {
    marginTop: 22,
    display: "grid",
    gap: 18,
    gridTemplateColumns: "repeat(auto-fit, minmax(320px, 1fr))",
  },
  panel: {
    borderRadius: 18,
    padding: 22,
    background: "rgba(0,0,0,0.34)",
    border: "1px solid rgba(255,255,255,0.10)",
    minHeight: 320,
  },
  panelEyebrow: {
    color: "rgba(255,255,255,0.94)",
    fontSize: 16,
    fontWeight: 800,
    marginBottom: 14,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  priceRow: {
    display: "flex",
    alignItems: "baseline",
    gap: 10,
    marginBottom: 18,
  },
  price: {
    color: "#fff",
    fontSize: 58,
    lineHeight: 1,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  priceSuffix: {
    color: "rgba(255,255,255,0.84)",
    fontSize: 18,
    textTransform: "uppercase",
    letterSpacing: 0.6,
  },
  panelText: {
    margin: "0 0 18px",
    color: "rgba(235,240,255,0.82)",
    lineHeight: 1.55,
    fontSize: 15,
  },
  label: {
    display: "block",
    color: "rgba(255,255,255,0.96)",
    fontWeight: 700,
    fontSize: 14,
    marginBottom: 10,
    textTransform: "uppercase",
    letterSpacing: 0.3,
  },
  input: {
    width: "100%",
    marginTop: 10,
    borderRadius: 14,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(9,9,12,0.72)",
    color: "#fff",
    padding: "14px 16px",
    outline: "none",
    boxSizing: "border-box",
    fontSize: 16,
  },
  primaryBtn: {
    width: "100%",
    marginTop: 14,
    border: 0,
    borderRadius: 14,
    padding: "16px 18px",
    background: "linear-gradient(180deg, #ff2020 0%, #e10000 100%)",
    color: "#fff",
    fontWeight: 800,
    fontSize: 17,
    cursor: "pointer",
    boxShadow: "0 14px 30px rgba(200,0,0,0.22)",
  },
  primaryBtnDisabled: {
    background: "linear-gradient(180deg, rgba(120,120,120,0.65) 0%, rgba(80,80,80,0.8) 100%)",
    boxShadow: "none",
    cursor: "not-allowed",
    opacity: 0.9,
  },
  statusBox: {
    marginTop: 16,
    borderRadius: 16,
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(255,255,255,0.03)",
    padding: 18,
  },
  statusTitle: {
    color: "rgba(255,255,255,0.74)",
    textTransform: "uppercase",
    fontSize: 13,
    letterSpacing: 0.6,
  },
  statusValue: {
    marginTop: 6,
    color: "#fff",
    fontSize: 30,
    lineHeight: 1.05,
    fontWeight: 800,
    fontFamily: 'Georgia, "Times New Roman", serif',
  },
  statusMeta: {
    marginTop: 8,
    color: "rgba(255,255,255,0.74)",
    fontSize: 14,
    textTransform: "capitalize",
  },
  actionRow: {
    display: "grid",
    gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
    gap: 12,
    marginTop: 16,
  },
  secondaryBtn: {
    borderRadius: 14,
    padding: "14px 16px",
    border: "1px solid rgba(255,255,255,0.10)",
    background: "rgba(0,0,0,0.28)",
    color: "#fff",
    fontWeight: 700,
    cursor: "pointer",
  },
  cancelScheduledBox: {
    marginTop: 10,
    borderRadius: 12,
    padding: "10px 12px",
    background: "rgba(255, 174, 0, 0.12)",
    border: "1px solid rgba(255, 188, 64, 0.28)",
    color: "rgba(255, 239, 205, 0.96)",
    lineHeight: 1.45,
    fontSize: 13,
  },
  cancelBtn: {
    borderRadius: 12,
    padding: "11px 14px",
    border: "1px solid rgba(255, 78, 78, 0.42)",
    background: "rgba(140, 0, 24, 0.28)",
    color: "rgba(255,235,238,0.98)",
    fontWeight: 800,
    cursor: "pointer",
  },
  list: {
    margin: "18px 0 0",
    paddingLeft: 20,
    color: "rgba(235,240,255,0.86)",
    lineHeight: 1.6,
  },
};
